import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Megaphone, Plus, Pencil, Trash2, ExternalLink, Tag, Image as ImageIcon, X } from "lucide-react";
import { api, apiError, fileUrl } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { useVendorPublications, useVendorProducts, useVendorFormations, useVendorRefresh } from "@/hooks/useVendor";
import { VImageUpload } from "@/components/vendor/VImageUpload";
import { AdminModal, Field, Pill, inputCls, textareaCls } from "@/pages/admin/ui";

const EMPTY = {
  title: "", description: "", body: "", image_url: "", images: [], button_label: "En profiter", link: "",
  product_id: "", formation_id: "", promo_code: "", discount_label: "", starts_at: "", ends_at: "", is_active: true,
};

const PUB_STATUS = {
  active: { c: "bg-emerald-500 text-white", t: "🟢 Active" },
  scheduled: { c: "bg-sky-500 text-white", t: "🕒 Programmée" },
  expired: { c: "bg-slate-400 text-white", t: "⛔ Expirée" },
  inactive: { c: "bg-slate-300 text-slate-700", t: "⚪ Inactif" },
};

export default function VendorPublications() {
  const { data: pubs, isLoading } = useVendorPublications();
  const { data: products } = useVendorProducts();
  const { data: formations } = useVendorFormations();
  const refresh = useVendorRefresh();
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [gallery, setGallery] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm(EMPTY); setEditId(""); setOpen(true); };
  const openEdit = (p) => {
    setForm({
      title: p.title, description: p.description || "", body: p.body || "", image_url: p.image_url || "",
      images: p.images || [], button_label: p.button_label || "En profiter", link: p.link || "",
      product_id: p.product_id || "", formation_id: p.formation_id || "", promo_code: p.promo_code || "",
      discount_label: p.discount_label || "", starts_at: p.starts_at ? p.starts_at.slice(0, 10) : "",
      ends_at: p.ends_at ? p.ends_at.slice(0, 10) : "", is_active: p.is_active,
    });
    setEditId(p.id); setOpen(true);
  };

  const addGalleryImage = (url) => { if (url) { setForm((f) => ({ ...f, images: [...f.images, url] })); setGallery(""); } };
  const removeGalleryImage = (i) => setForm((f) => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.title.trim()) { toast.error("Titre obligatoire"); return; }
    setSaving(true);
    const payload = {
      ...form,
      starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : "",
      ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : "",
    };
    try {
      if (editId) await api.put(`/vendor/publications/${editId}`, payload);
      else await api.post("/vendor/publications", payload);
      toast.success(editId ? "Publication mise à jour" : "Publication créée");
      setOpen(false); refresh(["vendor-publications"]);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const toggle = async (p) => { try { await api.put(`/vendor/publications/${p.id}/status`, { is_active: !p.is_active }); refresh(["vendor-publications"]); } catch (e) { toast.error(apiError(e)); } };
  const remove = async (id) => { if (!window.confirm("Supprimer cette publication ?")) return; try { await api.delete(`/vendor/publications/${id}`); toast.success("Supprimée"); refresh(["vendor-publications"]); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div className="space-y-5" data-testid="vendor-publications">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold text-ink">Publications & Promotions</h1>
          <p className="mt-1 text-sm text-slate-500">Créez des affiches et promotions affichées dans votre boutique publique.</p>
        </div>
        <button onClick={openNew} data-testid="add-publication-btn" className="flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-bold text-white hover:bg-brand-dark">
          <Plus className="h-4 w-4" /> Nouvelle publication
        </button>
      </div>

      {isLoading ? (
        <div className="flex h-32 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
      ) : !pubs?.length ? (
        <div className="rounded-2xl border border-dashed border-line bg-white p-10 text-center">
          <Megaphone className="mx-auto h-9 w-9 text-slate-300" />
          <p className="mt-3 text-sm text-slate-500">Aucune publication. Créez votre première promotion.</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {pubs.map((p) => (
            <div key={p.id} data-testid={`publication-${p.id}`} className="flex flex-col overflow-hidden rounded-2xl border border-line bg-white">
              <div className="relative h-36 bg-slate-100">
                {p.image_url ? <img src={fileUrl(p.image_url)} alt="" className="h-full w-full object-cover" /> : <span className="flex h-full items-center justify-center text-slate-300"><ImageIcon className="h-8 w-8" /></span>}
                {p.discount_label && <span className="absolute left-2 top-2 rounded-full bg-rose-500 px-2.5 py-1 text-xs font-black text-white">{p.discount_label}</span>}
                {(() => { const s = PUB_STATUS[p.schedule_status] || PUB_STATUS[p.is_active ? "active" : "inactive"]; return (
                  <button onClick={() => toggle(p)} data-testid={`toggle-publication-${p.id}`}
                    className={`absolute right-2 top-2 rounded-full px-2.5 py-1 text-[11px] font-bold ${s.c}`}>
                    {s.t}
                  </button>
                ); })()}
              </div>
              <div className="flex flex-1 flex-col p-4">
                <p className="font-display text-base font-bold text-ink">{p.title}</p>
                {p.description && <p className="mt-1 line-clamp-2 text-sm text-slate-500">{p.description}</p>}
                <div className="mt-2 flex flex-wrap gap-2">
                  {p.promo_code && <span className="inline-flex items-center gap-1 rounded-lg bg-brand-soft px-2 py-1 text-xs font-black text-brand"><Tag className="h-3 w-3" /> {p.promo_code}</span>}
                </div>
                <p className="mt-2 text-[11px] text-slate-400">{formatDate(p.created_at)}</p>
                {(p.starts_at || p.ends_at) && (
                  <p className="text-[11px] font-semibold text-slate-500" data-testid={`pub-window-${p.id}`}>
                    {p.starts_at ? `Du ${formatDate(p.starts_at)}` : "Dès maintenant"}{p.ends_at ? ` au ${formatDate(p.ends_at)}` : ""}
                  </p>
                )}
                <div className="mt-3 flex items-center gap-1.5 border-t border-line pt-3">
                  <button onClick={() => openEdit(p)} data-testid={`edit-publication-${p.id}`} className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-line py-2 text-xs font-bold text-ink hover:border-brand hover:text-brand"><Pencil className="h-3.5 w-3.5" /> Modifier</button>
                  <button onClick={() => remove(p.id)} data-testid={`delete-publication-${p.id}`} className="flex h-8 w-8 items-center justify-center rounded-lg border border-line text-rose-500 hover:bg-rose-50"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <AdminModal open={open} onOpenChange={setOpen} title={editId ? "Modifier la publication" : "Nouvelle publication"} wide>
        <div className="space-y-4">
          <Field label="Titre"><input value={form.title} onChange={(e) => set("title", e.target.value)} className={inputCls} data-testid="pub-title" placeholder="Offre spéciale de la semaine" /></Field>
          <Field label="Description courte"><textarea rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} className={textareaCls} data-testid="pub-description" /></Field>
          <Field label="Texte / détails"><textarea rows={3} value={form.body} onChange={(e) => set("body", e.target.value)} className={textareaCls} /></Field>
          <VImageUpload label="Image principale" value={form.image_url} onChange={(v) => set("image_url", v)} testId="pub-image" />

          <div>
            <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-wide text-slate-500">Images supplémentaires</span>
            {form.images.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-2">
                {form.images.map((img, i) => (
                  <div key={i} className="relative h-16 w-16 overflow-hidden rounded-lg border border-line">
                    <img src={fileUrl(img)} alt="" className="h-full w-full object-cover" />
                    <button onClick={() => removeGalleryImage(i)} className="absolute right-0.5 top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-white/90 text-slate-600"><X className="h-3 w-3" /></button>
                  </div>
                ))}
              </div>
            )}
            <VImageUpload label="" value={gallery} onChange={addGalleryImage} testId="pub-gallery" />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Réduction affichée"><input value={form.discount_label} onChange={(e) => set("discount_label", e.target.value)} className={inputCls} placeholder="-20%" data-testid="pub-discount" /></Field>
            <Field label="Code promo"><input value={form.promo_code} onChange={(e) => set("promo_code", e.target.value.toUpperCase())} className={`${inputCls} uppercase`} placeholder="PROMO20" data-testid="pub-promo" /></Field>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Produit associé (optionnel)">
              <select value={form.product_id} onChange={(e) => set("product_id", e.target.value)} className={inputCls} data-testid="pub-product">
                <option value="">— Aucun —</option>
                {(products || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
            <Field label="Formation associée (optionnel)">
              <select value={form.formation_id} onChange={(e) => set("formation_id", e.target.value)} className={inputCls} data-testid="pub-formation">
                <option value="">— Aucune —</option>
                {(formations || []).map((f) => <option key={f.id} value={f.id}>{f.title}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Libellé du bouton"><input value={form.button_label} onChange={(e) => set("button_label", e.target.value)} className={inputCls} /></Field>
            <Field label="Lien du bouton"><input value={form.link} onChange={(e) => set("link", e.target.value)} className={inputCls} placeholder="/boutique/mon-produit" /></Field>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Date de début"><input type="date" value={form.starts_at} onChange={(e) => set("starts_at", e.target.value)} className={inputCls} /></Field>
            <Field label="Date de fin"><input type="date" value={form.ends_at} onChange={(e) => set("ends_at", e.target.value)} className={inputCls} /></Field>
          </div>
          <label className="flex items-center gap-2 text-sm font-bold text-ink">
            <input type="checkbox" checked={form.is_active} onChange={(e) => set("is_active", e.target.checked)} className="h-4 w-4 accent-brand" data-testid="pub-active" /> Publier immédiatement (visible dans la boutique)
          </label>
          <button onClick={save} disabled={saving} data-testid="pub-save" className="w-full rounded-full bg-brand py-3 text-sm font-bold text-white disabled:opacity-60">
            {saving ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : "Enregistrer la publication"}
          </button>
        </div>
      </AdminModal>
    </div>
  );
}
